import React from 'react';
import { useAppSelector } from '../store';
import { 
  selectCentralizedPowerRequirements, 
  selectBasicSystemsPower,
  selectBridgePower,
  selectSensorsPower,
  selectWeaponsPower,
  selectDefensiveSystemsPower,
  selectStateroomPower,
  selectOptionalSystemsPower
} from '../utils/calculations/centralizedPowerCalculations';
import * as derivedSelectors from '../store/selectors/derivedSelectors';

export const PowerDebugger: React.FC = () => {
  const state = useAppSelector((state) => state);
  const powerRequirements = useAppSelector(selectCentralizedPowerRequirements);
  
  // Get individual power components
  const basicSystemsPower = useAppSelector(selectBasicSystemsPower);
  const bridgePower = useAppSelector(selectBridgePower);
  const sensorsPower = useAppSelector(selectSensorsPower);
  const weaponsPower = useAppSelector(selectWeaponsPower);
  const defensiveSystemsPower = useAppSelector(selectDefensiveSystemsPower);
  const stateroomPower = useAppSelector(selectStateroomPower);
  const optionalSystemsPower = useAppSelector(selectOptionalSystemsPower);
  const totalDrivePower = useAppSelector(derivedSelectors.selectTotalDrivePower);
  
  // Get power plant details
  const powerPlant = state.shipBuilder.powerPlant;
  const powerPlantPower = powerPlant?.power || 0;
  const powerPlantWeight = powerPlant?.weight || 0;
  const powerPlantType = powerPlant?.type || 'None';
  
  // Calculate individual drive power using correct Traveller rules
  const drives = state.shipBuilder.drives || [];
  const shipSize = state.shipBuilder.shipSize || 0;
  
  let maneuverDrivePower = 0;
  let jumpDrivePower = 0;
  let reactionDrivePower = 0;
  
  drives.forEach((drive) => {
    if (drive.rating >= 0) {
      switch (drive.type) {
        case 'maneuver':
          // Maneuver Drive: 10% of hull × thrust rating
          maneuverDrivePower += shipSize * 0.1 * drive.rating;
          break;
        case 'jump':
          // Jump Drive: 10% of hull × jump rating
          jumpDrivePower += shipSize * 0.1 * drive.rating;
          break;
        case 'reaction':
          // Reaction Drive: 0 Power (no power required)
          reactionDrivePower += 0;
          break;
      }
    }
  });
  
  // Calculate power efficiency
  const powerEfficiency = powerPlantPower > 0 ? (powerRequirements.maximumLoad / powerPlantPower) * 100 : 0;
  const powerSurplus = powerPlantPower - powerRequirements.maximumLoad;
  
  // Calculate operational power usage
  const normalOperationsPower = powerRequirements.normalOperations;
  const jumpOperationsPower = powerRequirements.jumpOperations;
  const maximumLoadPower = powerRequirements.maximumLoad;

  return (
    <div className="p-5 bg-gray-700 m-5 rounded-lg text-white">
      <h3 className="text-xl font-bold mb-4 text-white">Power Generation & Consumption Debug</h3>
      
      {/* Power Generation Section */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-blue-300">Power Generation</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><strong>Component</strong></div>
          <div><strong>Power</strong></div>
          
          <div>Power Plant ({powerPlantType}):</div>
          <div>{powerPlantPower.toFixed(0)} Power</div>
          
          <div>Power Plant Weight:</div>
          <div>{powerPlantWeight.toFixed(1)} tons</div>
          
          <div className="border-t-2 border-gray-500 pt-2"><strong>Total Available:</strong></div>
          <div className="border-t-2 border-gray-500 pt-2"><strong>{powerPlantPower.toFixed(0)} Power</strong></div>
        </div>
      </div>

      {/* Power Consumption Section */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-green-300">Power Consumption</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><strong>Component</strong></div>
          <div><strong>Power</strong></div>
          
          <div>Basic Systems (20% of hull):</div>
          <div>{basicSystemsPower.toFixed(0)} Power</div>
          
          <div>Maneuver Drive:</div>
          <div>{maneuverDrivePower.toFixed(0)} Power</div>
          
          <div>Jump Drive:</div>
          <div>{jumpDrivePower.toFixed(0)} Power</div>
          
          <div>Reaction Drive:</div>
          <div>{reactionDrivePower.toFixed(0)} Power</div>
          
          <div>Bridge:</div>
          <div>{bridgePower.toFixed(0)} Power</div>
          
          <div>Sensors:</div>
          <div>{sensorsPower.toFixed(0)} Power</div>
          
          <div>Weapons:</div>
          <div>{weaponsPower.toFixed(0)} Power</div>
          
          <div>Defensive Systems:</div>
          <div>{defensiveSystemsPower.toFixed(0)} Power</div>
          
          <div>Optional Systems:</div>
          <div>{optionalSystemsPower.toFixed(0)} Power</div>
          
          <div>Staterooms & Living:</div>
          <div>{stateroomPower.toFixed(0)} Power</div>
          
          <div className="border-t-2 border-gray-500 pt-2"><strong>Total Drive Power:</strong></div>
          <div className="border-t-2 border-gray-500 pt-2"><strong>{totalDrivePower.toFixed(0)} Power</strong></div>
          
          <div className="border-t-2 border-gray-500 pt-2"><strong>Additional Power:</strong></div>
          <div className="border-t-2 border-gray-500 pt-2"><strong>{powerRequirements.additionalPower.toFixed(0)} Power</strong></div>
        </div>
      </div>

      {/* Operational Power Requirements */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-yellow-300">Operational Power Requirements</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><strong>Operation Mode</strong></div>
          <div><strong>Power Required</strong></div>
          
          <div>Normal Operations:</div>
          <div>{normalOperationsPower.toFixed(0)} Power</div>
          
          <div>Jump Operations:</div>
          <div>{jumpOperationsPower.toFixed(0)} Power</div>
          
          <div>Maximum Load:</div>
          <div>{maximumLoadPower.toFixed(0)} Power</div>
          
          <div className="border-t-2 border-gray-500 pt-2"><strong>Power Plant Capacity:</strong></div>
          <div className="border-t-2 border-gray-500 pt-2"><strong>{powerPlantPower.toFixed(0)} Power</strong></div>
        </div>
      </div>

      {/* Power Analysis */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-purple-300">Power Analysis</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><strong>Metric</strong></div>
          <div><strong>Value</strong></div>
          
          <div>Power Efficiency:</div>
          <div className={powerEfficiency > 100 ? "text-red-400" : "text-green-400"}>
            {powerEfficiency.toFixed(1)}%
          </div>
          
          <div>Power Surplus/Deficit:</div>
          <div className={powerSurplus >= 0 ? "text-green-400" : "text-red-400"}>
            {powerSurplus >= 0 ? "+" : ""}{powerSurplus.toFixed(0)} Power
          </div>
          
          <div>Normal Operations Margin:</div>
          <div className={powerPlantPower >= normalOperationsPower ? "text-green-400" : "text-red-400"}>
            {powerPlantPower >= normalOperationsPower ? "+" : ""}{(powerPlantPower - normalOperationsPower).toFixed(0)} Power
          </div>
          
          <div>Jump Operations Margin:</div>
          <div className={powerPlantPower >= jumpOperationsPower ? "text-green-400" : "text-red-400"}>
            {powerPlantPower >= jumpOperationsPower ? "+" : ""}{(powerPlantPower - jumpOperationsPower).toFixed(0)} Power
          </div>
          
          <div>Maximum Load Margin:</div>
          <div className={powerPlantPower >= maximumLoadPower ? "text-green-400" : "text-red-400"}>
            {powerPlantPower >= maximumLoadPower ? "+" : ""}{(powerPlantPower - maximumLoadPower).toFixed(0)} Power
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="mt-5">
        <h4 className="text-lg font-semibold mb-3 text-white">Detailed Power Breakdown</h4>
        <div className="text-xs text-gray-300 space-y-1">
          <div><strong>Drive Power Details:</strong></div>
          <div>Total Drive Power: {totalDrivePower.toFixed(0)} Power</div>
          <div>Maneuver Drive: {maneuverDrivePower.toFixed(0)} Power (10% of hull × thrust rating)</div>
          <div>Jump Drive: {jumpDrivePower.toFixed(0)} Power (10% of hull × jump rating)</div>
          <div>Reaction Drive: {reactionDrivePower.toFixed(0)} Power (no power required)</div>
          <div className="mt-2"><strong>Additional Systems:</strong></div>
          <div>Bridge: {bridgePower.toFixed(0)} Power</div>
          <div>Sensors: {sensorsPower.toFixed(0)} Power</div>
          <div>Weapons: {weaponsPower.toFixed(0)} Power</div>
          <div>Defensive Systems: {defensiveSystemsPower.toFixed(0)} Power</div>
          <div>Optional Systems: {optionalSystemsPower.toFixed(0)} Power</div>
          <div>Staterooms & Living: {stateroomPower.toFixed(0)} Power</div>
          <div className="mt-2"><strong>Power Plant Details:</strong></div>
          <div>Type: {powerPlantType}</div>
          <div>Weight: {powerPlantWeight.toFixed(1)} tons</div>
          <div>Power Output: {powerPlantPower.toFixed(0)} Power</div>
        </div>
      </div>
    </div>
  );
};
